import {
    TeeTimeType,
    type CombinedObject,
    type NotificationObject,
} from "@golf-district/shared";
import { WeatherIcons } from "~/constants/weather-icons";
import { useCourseContext } from "~/contexts/CourseContext";
import { useFiltersContext } from "~/contexts/FiltersContext";
import { useDraggableScroll } from "~/hooks/useDraggableScroll";
import { api } from "~/utils/api";
import { googleAnalyticsEvent } from "~/utils/googleAnalyticsUtils";
import { dayMonthDate, dayMonthDateV2 } from "~/utils/formatters";
import { useEffect, useMemo, useRef, useState } from "react";
import { TeeTime } from "../cards/tee-time";
import { Info } from "../icons/info";
import { LeftChevron } from "../icons/left-chevron";
import { Tooltip } from "../tooltip";
import { TeeTimeSkeleton } from "./tee-time-skeleton";
import dayjs from "dayjs";
import { useAppContext } from "~/contexts/AppContext";
import { SafeContent } from "~/utils/safe-content";
import { useUserContext } from "~/contexts/UserContext";
import { useIntersectionObserver } from "usehooks-ts";

export const DailyTeeTimesDesktopV2 = ({
    minDate,
    maxDate,
    setError,
    handleLoading,
    dateType,
    dates,
    isLoadingTeeTimeDate
}: {
    minDate: string;
    maxDate: string;
    setError: (t: string | null) => void;
    handleLoading?: (val: boolean) => void;
    dateType: string,
    dates: string[],
    isLoadingTeeTimeDate: boolean
}) => {
    const overflowRef = useRef<HTMLDivElement>(null);
    const nextPageRef = useRef<HTMLLIElement>(null);
    const { onMouseDown } = useDraggableScroll(overflowRef, {
        direction: "horizontal",
    });
    const { user } = useUserContext();
    const [date, setDate] = useState<string>(dates[0] ?? '');
    const [isAtStart, setIsAtStart] = useState(true);
    const [isAtEnd, setIsAtEnd] = useState(false);
    const { course, getAllowedPlayersForTeeTime } = useCourseContext();
    const courseId = course?.id;
    const { entity } = useAppContext();
    const firstDate = dates[0];
    useEffect(() => {
        setDate(firstDate ?? '');
    }, [dateType, minDate, firstDate]);

    const { data: courseException } =
        api.courseException.getCourseException.useQuery({
            courseId: courseId ?? "",
        });

    const getCourseException = (playDate: string): null | NotificationObject => {
        let flag = false;
        let msg: NotificationObject | null = null;
        courseException?.forEach((ce) => {
            const startDate = new Date(ce.startDate);
            const endDate = new Date(ce.endDate);
            const dateToCheck = new Date(playDate);
            if (dateToCheck > startDate && dateToCheck < endDate) {
                flag = true;
                msg = ce;
            }
        });
        if (flag) {
            return msg;
        }
        return null;
    };

    const courseExceptions = getCourseException(date)

    const {
        showUnlisted,
        includesCart,
        golfers,
        holes,
        priceRange,
        startTime,
        sortValue,
    } = useFiltersContext();
    const teeTimeStartTime = startTime[0];
    const teeTimeEndTime = startTime[1];

    const allowedPlayers = useMemo(() => getAllowedPlayersForTeeTime(), [course]);

    const numberOfPlayers = allowedPlayers?.numberOfPlayers[0];

    const playersCount = numberOfPlayers ? Number(numberOfPlayers) : 0;

    const { data: weather, isLoading: isLoadingWeather } =
        api.searchRouter.getWeatherForDay.useQuery(
            {
                courseId: course?.id ?? "",
                date,
            },
            {
                enabled: course?.id !== undefined && date !== undefined,
            }
        );
    const TAKE = 250;
    const {
        data: teeTimeData,
        isLoading,
        isFetchingNextPage,
        fetchNextPage,
        error,
        refetch,
        hasNextPage,
    } = api.searchRouter.getTeeTimesForDay.useInfiniteQuery(
        {
            courseId: course?.id ?? "",
            date,
            minDate,
            maxDate,
            startTime: teeTimeStartTime,
            endTime: teeTimeEndTime,
            showUnlisted,
            includesCart,
            golfers: golfers === "Any" ? -1 : golfers,
            sortTime:
                sortValue === "Sort by time - Early to Late"
                    ? "asc"
                    : sortValue === "Sort by time - Late to Early"
                        ? "desc"
                        : "",
            sortPrice:
                sortValue === "Sort by price - Low to High"
                    ? "asc"
                    : sortValue === "Sort by price - High to Low"
                        ? "desc"
                        : "",
            timezoneCorrection: course?.timezoneCorrection,
            take: TAKE,
        },
        {
            getNextPageParam: (lastPage) => {
                if (!lastPage?.results || lastPage.results.length === 0) {
                    return undefined;
                }
                if (lastPage.results.length < TAKE) {
                    return undefined;
                }
                return lastPage?.cursor ?? undefined;
            },
            enabled: course?.id !== undefined && date !== undefined,
            refetchOnWindowFocus: false,
        }
    );

    useEffect(() => {
        setError(error?.message ?? null);
    }, [error]);

    // Flatten all pages into a single array
    const allTeeTimes = useMemo(() => {
        return teeTimeData?.pages.flatMap(page => page?.results ?? []) ?? [];
    }, [teeTimeData]);

    // Intersection observer for pagination
    const entry = useIntersectionObserver(nextPageRef, {});
    const isVisible = !!entry?.isIntersecting;

    useEffect(() => {
        if (isVisible && hasNextPage) {
            void getNextPage();
        }
    }, [isVisible, hasNextPage]);

    const getNextPage = async () => {
        if (!isLoading && !isFetchingNextPage && hasNextPage) {
            await fetchNextPage();
        }
    };

    const isScrolling = useRef(false);

    const scrollCarousel = (direction: "left" | "right") => {
        const container = overflowRef.current;
        if (!container) return;

        const boxWidth = 265;
        const containerWidth = container.clientWidth;

        const visibleCount = Math.floor(containerWidth / (boxWidth + 16));
        const scrollAmount = visibleCount * (boxWidth + 16);

        if (direction === "right") {
            container.classList.add("scroll-smooth");
            container.scrollBy({ left: scrollAmount, behavior: "smooth" });
            setTimeout(() => {
                isScrolling.current = false;
            }, 500);
        } else {
            container.classList.add("scroll-smooth");
            container.scrollBy({ left: -scrollAmount, behavior: "smooth" });
        }
        updateArrowState(container)
    };

    const scrollRight = () => {
        googleAnalyticsEvent({ action: "scrlright_ttlist" });
        scrollCarousel("right");
    };
    const scrollLeft = () => {
        googleAnalyticsEvent({ action: "scrlleft_ttlist" });
        scrollCarousel("left");
    };

    useEffect(() => {
        setIsAtStart(true);
        setIsAtEnd(false);
        if (overflowRef.current) {
            overflowRef.current.scrollLeft = 0;
        }
    }, [dateType]);

    const updateArrowState = (container: HTMLElement) => {
        if (!container) return;

        const isAtStart = container.scrollLeft === 0;
        const isAtEnd = Math.ceil(container.scrollLeft + container.clientWidth) >= container.scrollWidth;

        setIsAtStart(isAtStart);
        setIsAtEnd(isAtEnd);
    };
    useEffect(() => {
        const container = overflowRef.current;
        if (container) {
            updateArrowState(container);

            const handleScroll = () => {
                updateArrowState(container);
            };

            container.addEventListener("scroll", handleScroll);

            return () => {
                container.removeEventListener("scroll", handleScroll);
            };
        }
    }, [overflowRef]);

    const visibleTeeTimes = allTeeTimes.filter((i: CombinedObject) => {
        const shouldRender =
            i.firstOrSecondHandTeeTime === TeeTimeType.SECOND_HAND ||
            i.firstOrSecondHandTeeTime === TeeTimeType.OFFER ||
            i.availableSlots >= playersCount;
        const isOwnOffer =
            i.firstOrSecondHandTeeTime === TeeTimeType.OFFER && i.soldById === user?.id;
        return shouldRender && !isOwnOffer;
    });

    return (
        <div className="flex flex-col gap-1 md:gap-4 bg-white px-4 py-2 md:rounded-xl md:px-8 md:py-6">
            <div className="relative" >

                <div className="absolute top-1/2 md:block -translate-y-1/2 z-[2] flex items-center justify-center -left-1 md:-left-6">
                    <button
                        onClick={() => scrollLeft()}
                        className={`flex h-fit items-center justify-center rounded-full bg-white p-2 shadow-overflow-indicator ${isAtStart ? 'hidden' : ""}`}
                        data-testid="tee-time-left-chevron-id"
                        aria-label="Scroll Left"
                    // disabled={isAtStart}
                    >
                        <LeftChevron fill="var(--primary-color)" className="w-[1rem]" />
                    </button>
                </div>
                <div
                    className="scrollbar-none w-full flex overflow-x-auto overflow-y-hidden gap-4"
                    ref={overflowRef}
                    onMouseDown={onMouseDown}
                    style={{
                        scrollSnapType: "x mandatory",
                        scrollBehavior: "smooth",
                    }}
                >
                    <ul
                        style={{
                            position: "relative",
                            display: "flex",
                            margin: "0px",
                            padding: "0px",
                            listStyle: "none",
                            overflowY: "visible",
                            scrollbarWidth: "none",
                            scrollSnapType: "x mandatory",
                            scrollMarginInlineStart: "2.5em",
                            gap: 15
                        }}
                    >
                        {isLoadingTeeTimeDate
                            ? Array.from({ length: 16 }).map((_, idx) => (
                                <li
                                    key={idx}
                                    className="p-4 min-w-[10rem] border rounded-lg text-center bg-gray-200 animate-pulse"
                                >
                                    <div className="h-6 w-[50%] bg-gray-300 rounded-md mx-auto animate-pulse"></div>
                                </li>
                            ))
                            : dates?.map((elm: string, idx: number) => {
                                const isSelected = dayjs(date).format("YYYY-MM-DD") === dayjs(elm).format("YYYY-MM-DD");

                                return (
                                    <button
                                        key={idx}
                                        className={`p-4 min-w-[10rem] border rounded-lg text-center cursor-pointer ${isSelected ? "bg-primary text-white" : ""
                                            }`}
                                        onClick={() => setDate(elm)}
                                    >
                                        <div className={`text-gray-700 ${isSelected ? "text-white" : ""}`}>
                                            {dayMonthDateV2(elm)}
                                        </div>
                                    </button>
                                );
                            })}
                    </ul>
                </div>
                <div className="absolute z-[2] md:block top-1/2 -translate-y-1/2 flex items-center justify-center -right-1 md:-right-6">
                    <button
                        onClick={scrollRight}
                        className={`flex h-fit items-center justify-center rounded-full bg-white p-2 shadow-overflow-indicator ${isAtEnd || dates.length <= 5 ? 'hidden' : ""}`}
                        data-testid="tee-time-right-chevron-id"
                        aria-label="Scroll Right"
                    >
                        <LeftChevron fill="var(--primary-color)" className="w-[1rem] rotate-180" />
                    </button>
                </div>
            </div>
            <div className="flex flex-wrap justify-between gap-2 unmask-time">
                {isLoading ? (
                    <div className="h-8 min-w-[9.375rem] w-[20%] bg-gray-200 rounded-md  animate-pulse unmask-time" />
                ) : (
                    <div
                        id="tee-time-box"
                        className="text-base md:text-xl unmask-time"
                        data-testid="date-group-id"
                        data-qa={dayMonthDate(date)}
                    >
                        {dayMonthDate(date)}
                    </div>
                )}

                {courseExceptions && (
                    <div className="flex-1 flex items-center gap-1">
                        <p
                            style={{
                                backgroundColor: courseExceptions.bgColor,
                                color: courseExceptions.color,
                            }}
                            className="inline text-left text-[0.8125rem] md:text-lg"
                        >
                            {courseExceptions.shortMessage}
                        </p>

                        {courseExceptions.longMessage && (
                            <Tooltip
                                className="text-left"
                                trigger={
                                    <span className="cursor-pointer" title="More Info">
                                        <Info className="h-4 md:h-5" />
                                    </span>
                                }
                                content={SafeContent({ htmlContent: courseExceptions.longMessage })}
                            />
                        )}
                    </div>
                )}

                {isLoadingWeather && !weather ? (
                    <div className="h-8 w-[30%] bg-gray-200 rounded-md  animate-pulse" />
                ) : weather && !isLoadingWeather && (
                    <div className="flex items-center gap-1">
                        <div>{WeatherIcons[weather?.iconCode ?? ""]}</div>
                        <div
                            className="text-xs md:text-base unmask-temperature"
                            data-testid="weather-degrees-id"
                            data-qa={weather?.temperature}
                        >
                            {weather?.temperature !== 0 ? `${weather?.temperature}°F` : ""}
                        </div>
                        <div
                            className="hidden text-sm text-primary-gray md:block"
                            data-testid="weather-text-id"
                            data-qa={weather?.shortForecast}
                        >
                            {weather?.shortForecast ?? ""}
                        </div>
                    </div>
                )}
            </div>

            <div>
                <ul
                    className="grid gap-x-[22px] gap-y-5 m-0 p-0 list-none"
                    style={{
                        gridTemplateColumns: "repeat(auto-fill, minmax(18rem, 1fr))",
                    }}
                >
                    {!isLoading && visibleTeeTimes.length > 0
                        ? visibleTeeTimes.map((i: CombinedObject, idx: number) => (
                            <li key={`${i.teeTimeId}-${idx}`}>
                                <TeeTime
                                    time={i.date}
                                    key={idx}
                                    items={i}
                                    index={idx}
                                    canChoosePlayer={i.availableSlots > 0}
                                    availableSlots={i.availableSlots}
                                    players={i.firstOrSecondHandTeeTime === TeeTimeType.OFFER ? String(i.availableSlots) : String(4 - i.availableSlots)}
                                    firstHandPurchasePrice={i?.firstHandPurchasePrice}
                                    price={i.pricePerGolfer}
                                    isOwned={i?.isOwned}
                                    soldById={i?.soldById}
                                    soldByImage={i?.soldByImage}
                                    soldByName={i?.soldByName}
                                    teeTimeId={i?.teeTimeId}
                                    isLiked={i?.userWatchListed}
                                    status={i?.firstOrSecondHandTeeTime}
                                    minimumOfferPrice={i?.minimumOfferPrice}
                                    bookingIds={i?.bookingIds ?? []}
                                    listingId={i?.listingId}
                                    listedSlots={i?.listedSlots}
                                    handleLoading={handleLoading}
                                    refetch={refetch}
                                    groupId={i?.groupId}
                                    allowSplit={i?.allowSplit}
                                    hasPriceClass={i.hasPriceClass}
                                    priceClasses={i.priceClasses}
                                    defaultPriceClassId={i.priceClasses?.filter((pc) => pc.isPublic)[0]?.id}
                                    defaultHole={i.defaultHole}
                                    availableHoles={i.availableHoles}
                                />
                            </li>
                        ))
                        : !isLoading && (
                            <li className="col-span-full">
                                <div className="flex flex-col items-center justify-center py-12 text-center">
                                    <p className="text-sm md:text-base text-primary-gray">
                                        No tee times available
                                    </p>
                                </div>
                            </li>
                        )}
                    {/* Intersection observer trigger for next page */}
                    {hasNextPage && !isLoading && (
                        <li
                            ref={nextPageRef}
                        >
                            <div className="h-4 w-4" />
                        </li>
                    )}
                    {/* Loading skeletons */}
                    {(isLoading || isFetchingNextPage) &&
                        Array(8)
                            .fill(null)
                            .map((_, idx) => (
                                <li
                                    key={`skeleton-${idx}`}
                                >
                                    <TeeTimeSkeleton />
                                </li>
                            ))}
                </ul>
            </div>
        </div>
    );
};
